import React, { useState, useEffect } from 'react'
import * as api from '../../services/api.js'
import Card from '../../components/Card.jsx'
import TipBox from '../../components/TipBox.jsx'
import AppSelect from '../../components/AppSelect.jsx'

const AGE_OPTIONS = [
  { value: 'Young adult (14–18)',       label: 'Young adult (14–18)' },
  { value: 'New adult (18–25)',         label: 'New adult (18–25)' },
  { value: 'Adult (25–45)',             label: 'Adult (25–45)' },
  { value: 'General adult (all ages)', label: 'General adult (all ages)' },
  { value: 'Older adult (45+)',         label: 'Older adult (45+)' },
]
const TONE_OPTIONS = [
  { value: 'Dark and serious',        label: 'Dark and serious' },
  { value: 'Hopeful and warm',        label: 'Hopeful and warm' },
  { value: 'Witty and playful',       label: 'Witty and playful' },
  { value: 'Atmospheric and slow',    label: 'Atmospheric and slow' },
  { value: 'Tense and propulsive',    label: 'Tense and propulsive' },
  { value: 'Lyrical and reflective',  label: 'Lyrical and reflective' },
]

export default function Audience({ pid = 'w' }) {
  const [project, setProject] = useState({})
  useEffect(() => { api.getWritingProjectData(pid).then(d => setProject(d || {})) }, [pid])
  function set(field, value) {
    setProject(prev => {
      const next = { ...prev, [field]: value }
      api.saveWritingProjectData(pid, next)
      return next
    })
  }

  return (
    <Card label="Section 2" title="Your audience">
      <TipBox title="💡 Who are you writing for?" collapsible>
        <p>Knowing your reader shapes every decision — how much to explain, what to leave unsaid, how dark you can go, how playful your prose can be. A useful trick: picture one specific person reading your book. Not a market. A person.</p>
        <ul>
          <li><strong>Age range</strong> — affects language complexity and life experience you can assume</li>
          <li><strong>Genre familiarity</strong> — a well-read fantasy fan needs less world-building explanation than a newcomer</li>
          <li><strong>Emotional need</strong> — are they reading to escape, to feel understood, to be challenged?</li>
        </ul>
      </TipBox>

      <div className="w-form" style={{ marginTop: '1.25rem' }}>
        <div>
          <label className="w-label">Primary reader age range</label>
          <AppSelect
            value={project.age || ''}
            onChange={v => set('age', v)}
            options={AGE_OPTIONS}
            placeholder="Choose..."
          />
        </div>
        <div>
          <label className="w-label">Tone</label>
          <AppSelect
            value={project.tone || ''}
            onChange={v => set('tone', v)}
            options={TONE_OPTIONS}
            placeholder="Choose..."
          />
        </div>
        <div className="full">
          <label className="w-label">What emotional need does this book meet for your reader?</label>
          <textarea className="w-textarea" rows={2}
            placeholder="e.g. They want to feel seen in their grief. They want hope without false comfort..."
            value={project['reader-need'] || ''}
            onChange={e => set('reader-need', e.target.value)}
          />
        </div>
        <div className="full">
          <label className="w-label">Comparable books (what would your reader have already loved?)</label>
          <input className="w-input"
            placeholder="e.g. The Midnight Library, A Little Life, Piranesi..."
            value={project.comps || ''}
            onChange={e => set('comps', e.target.value)}
          />
        </div>
      </div>

      <TipBox title="Understanding your reader" collapsible>
        <ul>
          <li>Picture one specific person reading your book — not a demographic, a person. What do they need from this story?</li>
          <li>What has your reader already read and loved? Your book exists in conversation with those books.</li>
          <li>What emotional state do you want your reader in on the last page — and is that different from where they started?</li>
        </ul>
      </TipBox>
    </Card>
  )
}
